import React from 'react';
import { flexRender, HeaderGroup } from '@tanstack/react-table';
import classNames from 'classnames';

import Icon, { faSortDown, faSortUp } from '~components/icon';

import { TableActions } from './types';

interface ComponentProps {
  headerGroups: HeaderGroup<any>[];
  allowSort?: boolean;
  tableActions?: Pick<TableActions, 'canEdit' | 'canSelect' | 'canDelete'>;
}

const getTitle = (
  getNextSortingOrder: () => 'asc' | 'desc' | false | undefined,
  getCanSort: () => boolean,
): string | undefined => {
  if (!getCanSort()) return undefined;

  const nextSortOrder = getNextSortingOrder();

  switch (nextSortOrder) {
    case 'asc':
      return 'Sort ascending';
    case 'desc':
      return 'Sort descending';
    case false:
      return 'Clear sort';
    default:
      return 'Clear sort';
  }
};

const TableHeader: React.FC<ComponentProps> = ({ headerGroups, allowSort, tableActions }) => {
  const { canSelect, canEdit, canDelete } = tableActions ?? {};

  return (
    <thead>
      {headerGroups.map(({ id: groupId, headers }) => (
        <tr key={groupId}>
          {canSelect && <th className="table-header-cell" />}
          {headers.map((header) => {
            if (header.isPlaceholder) return null;

            const { id, column, colSpan, getContext } = header;

            const { getToggleSortingHandler, getNextSortingOrder, getCanSort, getIsSorted } = column;

            const meta = column.columnDef.meta ?? {};

            const thClass = classNames('header-content', meta?.style?.header, {
              selectable: getCanSort(),
            });

            return (
              <th
                key={id}
                colSpan={colSpan}
                className="table-header-cell"
                style={{
                  width: meta?.style?.width,
                  minWidth: meta?.style?.minWidth,
                }}
              >
                <div
                  className={thClass}
                  onClick={getToggleSortingHandler()}
                  title={getTitle(getNextSortingOrder, getCanSort)}
                >
                  {flexRender(column.columnDef.header, getContext())}
                  {allowSort &&
                    ({
                      asc: <Icon icon={faSortUp} className="sort-icon" />,
                      desc: <Icon icon={faSortDown} className="sort-icon" />,
                    }[getIsSorted() as string] ??
                      null)}
                </div>
              </th>
            );
          })}
          {(canEdit || canDelete) && <th className="table-header-cell with-width" />}
        </tr>
      ))}
    </thead>
  );
};

export default TableHeader;
